<template>
  <div class="pa-4">
    <div v-allowed="'manage_documents'">
      <v-tabs v-model="activeTab" show-arrows>
        <v-tab :key="'completed'" ripple @click="clickTab(0)">{{ $t("message.completed_documents") }}</v-tab>
        <v-tab :key="'documents'" ripple @click="clickTab(1)">{{ $t("message.documents") }}</v-tab>
        <v-tab :key="'packages'" ripple @click="clickTab(2)">{{ $t("message.document_packages") }}</v-tab>
        <v-tab :key="'email_templates'" ripple @click="clickTab(3)">{{ $t("message.email_templates") }}</v-tab>
        <v-tab :key="'visitor_waiver'" ripple @click="clickTab(4)">{{ $t("message.visitor_waiver") }}</v-tab>

        <v-tabs-items v-model="activeTab">
          <v-tab-item :key="'completed'" class="pa-3">
            <v-card elevation="0" class="pa-2">
              <v-card-text class="pa-0 mt-4">
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <v-autocomplete outlined dense @input="event => { $emit('input', event) }" v-model="docNameFilter"
                      :items="documentsList" :label="$t('message.filter_by_document')" item-text="text"
                      item-value="value" clearable></v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <v-text-field dense outlined :label="$t('message.filter_by_member')"
                      v-model="userFilter"></v-text-field>
                  </v-col>
                </v-row>
                <v-row class="mt-0">
                  <v-col class="mt-0" cols="12" sm="12" md="12">
                    <v-switch class="mt-0" v-model="missingRequiredDocumentsFilter"
                      @change="showMissingRequiredDocuments"
                      :label="$t('message.missing_required_documents')"></v-switch>
                  </v-col>
                </v-row>
              </v-card-text>
              <div v-if="missingRequiredDocumentsFilter">
                <div class="text-center" v-if="!missingRequiredDocuments">
                  <CirclesLoader></CirclesLoader>
                </div>
                <v-list v-else-if="missingRequiredDocuments.length > 0">
                  <v-list-group class="pa-0" v-for="(template) in missingRequiredDocuments"
                    :key="'template_group_' + template._id" :value="false">
                    <template v-slot:activator>
                      <v-list-item-title>
                        {{ isSmallerDisplay() ? truncate(template.name, 26, false) : template.name }} <span
                          class="ml-1 gray--text">{{ template.missing }}</span>
                      </v-list-item-title>
                    </template>
                    <div v-if="template.missingMembers">
                      <v-list-item v-for="(member) in template.missingMembers"
                        :key="'template_group_member_' + template._id + '_' + member._id" link
                        @click="goToMember(member._id)">
                        <v-list-item-avatar>
                          <img v-if="member.userImageSmall" :src="userImg(member.userImageSmall)" />
                          <img v-else :src="assets('default-profile-avatar.png')" />
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ member.displayName }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </div>
                  </v-list-group>
                </v-list>
                <div v-else class="text-center text-body-1">
                  {{ $t('message.no_missing_documents') }}
                </div>
              </div>
              <v-card elevation="0" outlined v-else-if="filteredCompletedDocuments">
                <v-data-table :loading="loadingCompletedDocuments" item-key="_id" :headers="headers"
                  :items="filteredCompletedDocuments" class="elevation-0" :no-data-text="$t('message.no_data')"
                  :footer-props="{
                    itemsPerPageText: $t('message.rows_per_page'),
                    itemsPerPageOptions: [25, 50, 75]
                  }">
                  <template v-slot:item.user="{ item }">
                    <span v-if="item.user" class="clickable" @click="goToUser(item.user._id)">
                      <v-icon color="primary">mdi-account-circle-outline</v-icon>
                      {{ item.user.displayName }}
                    </span>
                    <span v-else class="clickable" @click="goToVisitor(item.memberData)">
                      <v-icon :color="item.memberData ? 'primary' : ''">mdi-target-account</v-icon>
                      {{ item.userName }}
                    </span>
                  </template>
                  <template v-slot:item.template="{ item }">
                    <span v-if="item.template">{{ item.template.name }}</span>
                    <span v-else>{{ $t('message.main_tablet_waiver') }}</span>
                  </template>
                  <template v-slot:item.time="{ item }">{{ $d(new Date(item.time), 'month_day_year', $i18n.locale)
                    }}</template>
                  <template v-slot:item.actions="{ item }">
                    <v-menu offset-y>
                      <template v-slot:activator="{ on }">
                        <v-btn small v-on="on" icon slot="activator" left style="margin-left:-8px">
                          <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list>
                        <v-list-item v-if="item.template && item.template.survey" @click="showAnswers(item)">
                          <v-list-item-title>
                            <v-icon class="mr-1">mdi-card-search-outline</v-icon>
                            {{ $t('message.view') }}
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item v-else @click="viewDocument(item)">
                          <v-list-item-title>
                            <v-icon class="mr-1">
                              mdi-card-search-outline
                            </v-icon>
                            {{ $t('message.view') }}
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item
                          v-if="(!item.template || (item.template && !item.template.survey)) && !isSmallerDisplay()"
                          @click="print(item)">
                          <v-list-item-title>
                            <v-icon class="mr-1">
                              mdi-printer-outline
                            </v-icon>
                            {{ $t('message.print') }}
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="selectedDocument = item; archiveDialog = true;">
                          <v-list-item-title>
                            <v-icon>mdi-archive-arrow-down-outline</v-icon>
                            {{ $t('message.archive') }}
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </template>
                </v-data-table>
              </v-card>
            </v-card>
          </v-tab-item>
          <v-tab-item :key="'documents'" class="pa-3">
            <v-card elevation="0">
              <v-card-actions>
                <h3 style="font-weight: 400;">{{ $t('message.documents_explained') }}</h3>
                <v-spacer></v-spacer>
                <v-btn outlined small elevation="0" v-allowed="'create_waiver'" @click="goToDoc('new', null)" color="primary">
                  {{ $t('message.add') }}</v-btn>
              </v-card-actions>
              <v-card elevation="0" outlined v-if="documents">
                <v-data-table :loading="loadingDocuments" item-key="_id" :headers="documentsHeaders" :items="documents"
                  class="elevation-0" :no-data-text="$t('message.no_data')" :footer-props="{
                    itemsPerPageText: $t('message.rows_per_page'),
                    itemsPerPageOptions: [25, 50, 75]
                  }">
                  <template v-slot:item.name="{ item }">
                    <span class="clickable" @click="goToDoc(item._id, item.name)">{{ item.name }}</span>
                  </template>
                  <template v-slot:item.user="{ item }">
                    <span class="clickable">{{ item.user ? item.user.displayName :
                      $t('message.system') }}</span>
                  </template>
                  <template v-slot:item.active="{ item }">
                    <v-icon color="primary" v-if="item.active">mdi-check-circle-outline</v-icon>
                    <v-icon v-else>mdi-close-circle-outline</v-icon>
                  </template>
                  <template v-slot:item.required="{ item }">
                    <v-icon color="primary" v-if="item.required">mdi-check-circle-outline</v-icon>
                    <v-icon v-else>mdi-close-circle-outline</v-icon>
                  </template>
                  <template v-slot:item.survey="{ item }">
                    <v-icon color="primary" v-if="item.survey">mdi-check-circle-outline</v-icon>
                    <v-icon v-else>mdi-close-circle-outline</v-icon>
                  </template>
                  <template v-slot:item.contract="{ item }">
                    <v-icon color="primary" v-if="item.contract">mdi-check-circle-outline</v-icon>
                    <v-icon v-else>mdi-close-circle-outline</v-icon>
                  </template>
                  <template v-slot:item.public="{ item }">
                    <v-icon color="primary" v-if="item.public">mdi-check-circle-outline</v-icon>
                    <v-icon v-else>mdi-close-circle-outline</v-icon>
                  </template>
                  <template v-slot:item.lastUpdatedOn="{ item }">{{ $d(new Date(item.lastUpdatedOn), 'month_day_year',
                    $i18n.locale)
                    }}</template>
                  <template v-slot:item.actions="{ item }">
                    <v-menu offset-y v-if="item.survey">
                      <template v-slot:activator="{ on }">
                        <v-btn v-on="on" fab text small slot="activator" left style="margin-left:-8px">
                          <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list>
                        <v-list-item @click.stop="goToDoc(item._id, item.name)">
                          <v-list-item-title>
                            <v-icon>mdi-pencil-box-outline</v-icon>
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item @click.stop="goToAggregates(item._id)">
                          <v-list-item-title>
                            <v-icon>mdi-chart-pie-outline</v-icon>
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                    <v-icon v-else title="Edit" @click="goToDoc(item._id, item.name)">mdi-pencil-box-outline</v-icon>
                  </template>
                </v-data-table>
              </v-card>
            </v-card>
          </v-tab-item>
          <v-tab-item :key="'packages'" class="pa-3">
            <v-card elevation="0">
              <v-card-actions>
                <div class="text-subtitle-1">{{ $t('message.member_packages_explained') }}</div>
                <v-spacer></v-spacer>
                <v-btn outlined elevation="0" v-allowed="'create_waiver'" @click="goToPackage('new')" color="primary"
                  small>{{
                    $t('message.add') }}</v-btn>
              </v-card-actions>
              <v-card elevation="0" outlined v-if="packages">
                <v-data-table item-key="_id" :headers="packagesHeaders" :items="packages" class="elevation-0"
                  :no-data-text="$t('message.no_data')" :footer-props="{
                    itemsPerPageText: $t('message.rows_per_page'),
                    itemsPerPageOptions: [25, 50, 75]
                  }">
                  <template v-slot:item.name="{ item }">
                    <span class="clickable" @click="goToPackage(item._id)">{{ item.name }}</span>
                  </template>
                  <template v-slot:item.user="{ item }">
                    <span class="clickable">{{ item.user.displayName }}</span>
                  </template>
                  <template v-slot:item.active="{ item }">
                    <v-icon color="primary" v-if="item.active">mdi-check-circle-outline</v-icon>
                    <v-icon v-else>mdi-close-circle-outline</v-icon>
                  </template>
                  <template v-slot:item.automatic="{ item }">
                    <v-icon color="primary" v-if="item.automatic">mdi-check-circle-outline</v-icon>
                    <v-icon v-else>mdi-close-circle-outline</v-icon>
                  </template>
                  <template v-slot:item.templates="{ item }">
                    {{ item.templates ? item.templates.length : 0 }}
                  </template>
                  <template v-slot:item.lastUpdatedOn="{ item }">{{ $d(new Date(item.lastUpdatedOn), 'month_day_year',
                    $i18n.locale)
                    }}</template>
                  <template v-slot:item.actions="{ item }">
                    <v-icon @click="goToPackage(item._id)">mdi-pencil-box-outline</v-icon>
                  </template>
                </v-data-table>
              </v-card>
            </v-card>
          </v-tab-item>
          <v-tab-item :key="'email_templates'" class="pa-3">
            <v-card elevation="0">
              <v-card-actions>
                <h3 style="font-weight: 400;">{{ $t('message.email_templates_explained') }}</h3>
                <v-spacer></v-spacer>
                <v-btn elevation="0" v-allowed="'create_waiver'" @click="goToEmailTemplate('new')" outlined
                  color="primary" small>{{
                    $t('message.add') }}</v-btn>
              </v-card-actions>
              <v-card elevation="0" outlined v-if="emailTemplates">
                <v-data-table item-key="_id" :headers="emailTemplateHeaders" :items="emailTemplates" class="elevation-0"
                  :no-data-text="$t('message.no_data')" :footer-props="{
                    itemsPerPageText: $t('message.rows_per_page'),
                    itemsPerPageOptions: [25, 50, 75]
                  }">
                  <template v-slot:item.name="{ item }">
                    <span class="clickable" @click="goToEmailTemplate(item._id)">{{ item.name }}</span>
                  </template>
                  <template v-slot:item.user="{ item }">
                    <span class="clickable">{{ item.user.displayName }}</span>
                  </template>
                  <template v-slot:item.lastUpdatedOn="{ item }">{{ $d(new Date(item.lastUpdatedOn), 'month_day_year',
                    $i18n.locale)
                    }}</template>
                  <template v-slot:item.actions="{ item }">
                    <v-icon @click="goToEmailTemplate(item._id)">mdi-pencil-box-outline</v-icon>
                  </template>
                </v-data-table>
              </v-card>
            </v-card>
          </v-tab-item>
          <v-tab-item :key="'visitor_waiver'" class="pa-3">
            <v-card elevation="0">
              <v-card-title>{{ $t('message.main_waiver_explained') }}</v-card-title>
              <v-card-text>
                <v-switch v-model="publicWaiver" @change="updatePublicWaiver"
                  :label="$t('message.public_waiver')"></v-switch>
                <span class="supporting-text">{{ $t('message.public_waiver_exaplained') }}</span>
              </v-card-text>
              <v-card-text v-if="publicWaiverUrl">
                <v-list-item class="pa-0">
                  <v-text-field :disabled="true" class="clickable" :label="$t('message.copy_public_waiver_url')"
                    v-model="publicWaiverUrl"></v-text-field>
                  <span class="mr-1">
                    <v-icon class="clickable" @click="copyPublicUrl()">mdi-content-copy</v-icon>
                  </span>
                  <span class="mr-1">
                    <v-icon class="clickable" @click="openQrCode()">mdi-qrcode</v-icon>
                  </span>
                  <span>
                    <v-icon class="clickable" @click="copyEmbedCode()">mdi-code-block-tags</v-icon>
                  </span>
                </v-list-item>
                <span class="supporting-text">{{ $t('message.public_waiver_url_exaplained') }}</span>
              </v-card-text>
              <v-card-actions>
                <template v-for="(w) in templates">
                  <v-btn class="ml-2" elevation="0" @click="swicthWaiver(w.lang)"
                    :color="w.lang == selectedWaiverLang ? themeColor('secondary') : 'primary'" fab small dark
                    :key="w.lang">{{ w.lang }}</v-btn>
                </template>
                <v-spacer></v-spacer>
                <v-spacer></v-spacer>
                <div style="width:100px">
                  <v-text-field :label="$t('message.language')" outline v-model="newWaiverLang"></v-text-field>
                </div>
                <v-btn class="ml-1" elevation="0" @click="addNewWaiverLang()" fab small>
                  <v-icon>mdi-plus-circle-outline</v-icon>
                </v-btn>
              </v-card-actions>
              <v-card-text dark>
                <vue-editor v-model="waiverContents" :editor-toolbar="editorToolbar" />
              </v-card-text>
              <v-card-text v-if="selectedWaiver && selectedWaiver.lastUpdatedBy">
                <v-avatar size="20">
                  <img v-if="selectedWaiver.lastUpdatedBy.userImageSmall"
                    :src="userImg(selectedWaiver.lastUpdatedBy.userImageSmall)" />
                  <img v-else :src="assets('default-profile-avatar.png')" />
                </v-avatar>
                {{ $t('message.last_update_by') }}
                {{ selectedWaiver.lastUpdatedBy.displayName }}
                <span style="color:gray">{{ $d(new Date(selectedWaiver.lastUpdatedOn), 'short', $i18n.locale) }}</span>
              </v-card-text>
              <v-card-text class="text-left">
                <div>
                  <a @click="displayExtraFieldsDiv = !displayExtraFieldsDiv">
                    {{ $t('message.add_custom_fields') }}
                  </a>
                </div>
                <v-card class="pa-2" outlined elevation="0" v-if="displayExtraFieldsDiv">
                  <v-btn elevation="0" class="mt-4" small @click="addNewExraField()">
                    <v-icon>mdi-plus-circle-outline</v-icon>{{ $t('message.add') }}
                  </v-btn>
                  <v-list two-line>
                    <template v-for="(field, index) in waiverExtraFields">
                      <v-list-item :key="'extra_field_' + index">
                        <v-list-item-action>
                          <span class="text-subtitle-1">{{ index + 1 }}</span>
                        </v-list-item-action>
                        <v-list-item-content class="text-left">
                          <v-list-item-title>
                            <v-text-field :label="$t('message.name')" v-model="field.fieldName"></v-text-field>
                          </v-list-item-title>
                          <div class="mt-2">
                            <v-select :label="$t('message.answer_type')" :items="waiverFieldTypes"
                              v-model="field.fieldType" dense></v-select>
                          </div>
                          <div v-if="field.fieldType == 'multiple_choice'">
                            <div class="text-caption mb-4">{{ $t('message.waiver_field_options_hint') }}</div>
                            <v-text-field v-for="(o, oIndex) in field.options" :key="'ef_opt_' + index + '_' + oIndex"
                              :label="$t('message.answer_option') + ' ' + (oIndex + 1)"
                              v-model="field.options[oIndex]" maxlength="30" counter dense>
                            </v-text-field>
                            <v-btn elevation="0" small @click="addFieldOption(field)">
                              <v-icon size="20">mdi-plus-circle-outline</v-icon>{{ $t('message.add') }}
                            </v-btn>
                          </div>
                          <v-list-item-subtitle class="mt-0 pt-0">
                            <v-checkbox :label="$t('message.required')" v-model="field.required"></v-checkbox>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn elevation="0" icon small @click="removeExtraField(index)">
                            <v-icon size="20">mdi-trash-can-outline</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider v-if="index + 1 < waiverExtraFields.length" :key="index"></v-divider>
                    </template>
                  </v-list>
                </v-card>
              </v-card-text>
              <v-card-actions>
                <v-switch v-model="waiverActive" :label="$t('message.active')"></v-switch>
              </v-card-actions>
              <v-card-actions>
                <v-btn outlined small elevation="0" v-if="adding" color="primary" @click="updateWaiver()">{{
                  $t('message.add')
                  }}</v-btn>
                <v-btn outlined small elevation="0" v-else color="primary" @click="updateWaiver()">{{
                  $t('message.update')
                  }}</v-btn>
                <v-btn v-if="adding" small @click="cancelAdding()">{{ $t('message.cancel') }}</v-btn>
                <v-btn elevation="0" small v-else color="error" @click="removeWaiverDialog = true;">{{
                  $t('message.delete') }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn small outlined elevation="0" color="primary" @click="showPrintable()">
                  <v-icon class="mr-1">mdi-printer-outline</v-icon>
                  {{ $t('message.printable_document') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </div>
    <v-dialog v-model="removeWaiverDialog" max-width="90%">
      <v-card class="pa-2">
        <v-card-text>{{ $t('message.are_you_sure') }}</v-card-text>
        <v-card-actions>
          <v-btn elevation="0" color="error" @click.stop="removeWaiver()">{{ $t('message.delete') }}</v-btn>
          <v-btn elevation="0" color="secondary" @click.stop="removeWaiverDialog = false">{{ $t('message.close')
            }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="archiveDialog" max-width="50%">
      <v-card>
        <v-card-title class="text-title">
          {{ $t('message.archive') }}
        </v-card-title>
        <v-card-text class="text-subtitle-1">
          {{ $t('message.are_you_sure') }}
        </v-card-text>
        <v-card-actions>
          <v-btn small elevation="0" color="error" @click.stop="archiveDocument()">{{ $t('message.archive') }}</v-btn>
          <v-btn class="outlined-btn" small elevation="0" @click.stop="archiveDialog = false">{{ $t('message.close')
            }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CirclesLoader from "@/components/displays/CirclesLoader.vue";
import Api from "@/services/Api2";
import { Environment } from "@/services/Environment";
import { VueEditor } from "vue2-editor";

export default {
  components: {
    VueEditor,
    CirclesLoader
  },
  data() {
    return {
      urlTab: this.$route.params.tabId,
      loading: true,
      completedDocuments: null,
      filteredCompletedDocuments: null,
      loadingCompletedDocuments: false,
      templates: null,
      packages: [],
      adding: false,
      activeTab: 0,
      waiverActive: false,
      missingRequiredDocumentsFilter: false,
      missingRequiredDocuments: null,
      languages: [],
      documents: [],
      displayExtraFieldsDiv: false,
      waiverExtraFields: [],
      waiverFieldTypes: [
        { text: this.$t("message.text"), value: "text" },
        { text: this.$t("message.multiple_choice"), value: "multiple_choice" },
      ],
      publicWaiver: false,
      archiveDialog: null,
      documentsList: [],
      selectedWaiver: null,
      waiverContents: null,
      approvedMembers: [],
      publicWaiverUrl: null,
      loadingDocuments: false,
      docNameFilter: null,
      userFilter: null,
      removeWaiverDialog: false,
      selectedWaiverLang: null,
      selectedDocument: null,
      newWaiverLang: null,
      editorToolbar: this.getEditorToolbar(true),
      documentsHeaders: [
        { text: this.$t("message.name"), value: "name" },
        { text: this.$t("message.creator"), value: "user" },
        { text: this.$t("message.updated"), value: "lastUpdatedOn" },
        { text: this.$t("message.active"), value: "active" },
        { text: this.$t("message.required"), value: "required" },
        { text: this.$t("message.survey"), value: "survey" },
        { text: this.$t("message.public"), value: "public" },
        { text: this.$t("message.contract"), value: "contract" },
        {
          text: "",
          align: "left",
          sortable: false,
          value: "actions",
        },
      ],
      packagesHeaders: [
        { text: this.$t("message.name"), value: "name" },
        { text: this.$t("message.creator"), value: "user" },
        { text: this.$t("message.updated"), value: "lastUpdatedOn" },
        { text: this.$t("message.active"), value: "active" },
        { text: this.$t("message.automatic"), value: "automatic" },
        { text: this.$t("message.templates"), value: "templates" },
        {
          text: "",
          align: "left",
          sortable: false,
          value: "actions",
        },
      ],
      emailTemplates: null,
      emailTemplateHeaders: [
        { text: this.$t("message.name"), value: "name" },
        { text: this.$t("message.subject"), value: "subject" },
        { text: this.$t("message.updated"), value: "lastUpdatedOn" },
        {
          text: "",
          align: "left",
          sortable: false,
          value: "actions",
        },
      ],
      headers: [
        { text: this.$t("message.participant_name"), value: "user" },
        { text: "Waiver", value: "template" },
        { text: this.$t("message.email"), value: "email" },
        { text: this.$t("message.date"), value: "time" },
        {
          text: "",
          align: "left",
          sortable: false,
          value: "actions",
        },
      ],
    };
  },
  mounted() {
    if (this.urlTab) {
      try {
        const n = new Number(this.urlTab);
        this.activeTab = n.valueOf();
      } catch (err) {
        this.activeTab = 0;
      }
    }
  },
  created() {
    this.getEmailTemplates();
    this.loading = false;
    this.getTemplates(this.$i18n.locale);
    this.getDocumentTemplates();
    this.getDocumentPackages();
    this.getCompletedDocuments();
    Api()
      .get("admins/academy/" + this.$store.state.selectedAcademy)
      .then((response) => {
        if (response.data) {
          this.approvedMembers = response.data.approvedMembers;
          this.publicWaiver = response.data.publicWaiver;
          this.publicWaiverUrl = 'https://core.bjjlink.com/pub/academy/' + this.$store.state.selectedAcademy + '/waiver';
        }
      });
  },
  watch: {
    docNameFilter: {
      handler: function () {
        this.filterCompletedDocuments();
      },
    },
    userFilter: {
      handler: function () {
        this.filterCompletedDocuments();
      },
    }
  },
  methods: {
    addFieldOption(field) {
      if (!field.options) this.$set(field, "options", []);
      field.options.push("");
    },
    removeExtraField(index) {
      this.waiverExtraFields.splice(index, 1);
    },
    addNewExraField() {
      this.waiverExtraFields.push({ fieldName: null, required: false, fieldType: "text", options: ["", ""] });
    },
    getEmailTemplates() {
      Api()
        .get(
          "documents/academy/" + this.$store.state.managedAcademy + "/email/templates?archived=false"
        )
        .then((response) => {
          if (response) {
            this.emailTemplates = response.data.templates;
          }
        });
    },
    getMissingDocumentMembers(position) {
      let template = this.missingRequiredDocuments[position];
      if (template && !template.missingMembers) {
        Api()
          .get(
            "documents/academy/" + this.$store.state.managedAcademy + "/required/missing/template/" + template._id
          )
          .then((response) => {
            if (response) {
              template.missingMembers = response.data.members;
              this.missingRequiredDocuments[position] = template;
            }
          });
      }
    },
    showMissingRequiredDocuments() {
      this.userFilter = null;
      this.docNameFilter = null;
      if (this.missingRequiredDocumentsFilter) {
        Api()
          .get(
            "documents/academy/" + this.$store.state.managedAcademy + "/required/missing/all"
          )
          .then((response) => {
            if (response)
              this.missingRequiredDocuments = response.data.results;
          });
      } else {
        this.missingRequiredDocuments = null;
      }
    },
    openQrCode() {
      let buff = new Buffer(this.publicWaiverUrl);
      const encodedUrl = buff.toString("base64");
      if (this.isSmallerDisplay()) {
        const baseUrl = Environment.get("VUE_APP_UI_HOST");
        this.copyToClipboard(baseUrl + Environment.get("VUE_APP_ASSETS_HOME") + 'secure/qr/' + encodedUrl + '?sp=waiver');
      } else {
        window.open(Environment.get("VUE_APP_ASSETS_HOME") + 'secure/qr/' + encodedUrl + '?sp=waiver');
      }
    },
    clickTab(tab) {
      if (tab != this.activeTab) {
        this.selectedItems = [];
        this.navigateTo({
          name: "waiverstab",
          params: { tabId: tab },
        });
      }
    },
    copyPublicUrl() {
      let urlToCopy = this.publicWaiverUrl;
      this.copyToClipboard(urlToCopy);
    },
    copyEmbedCode() {
      let textToCopy = '<iframe frameBorder="0" style="width:100%;height:330px" src="' + this.publicWaiverUrl + '"></iframe>';
      this.copyToClipboard(textToCopy);
    },
    getPackages() {

    },
    goToAggregates(id) {
      this.navigateTo({ name: "viewsurveyaggregates", params: { templateId: id } });
    },
    showAnswers(item) {
      this.navigateTo({ name: "viewsurveyanswers", params: { documentId: item._id } });
    },
    updatePublicWaiver() {
      Api()
        .patch(
          "admins/academy/" + this.$store.state.managedAcademy + "/publicWaiver",
          { publicWaiver: this.publicWaiver }
        )
        .then((response) => {
          if (response)
            this.showAlert("success", this.$t("message.updated"));
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    getCompletedDocuments() {
      this.loading = true;
      this.loadingCompletedDocuments = true;
      Api()
        .post(
          "documents/academy/" + this.$store.state.managedAcademy + "/completed"
        )
        .then((response) => {
          this.loading = false;
          const data = response.data;
          if (data) {
            this.completedDocuments = data;
            this.filterCompletedDocuments();
            this.loadingCompletedDocuments = false;
          }
        })
        .catch((e) => {
          this.loading = false;
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    archiveDocument() {
      Api()
        .delete(
          "documents/academy/" + this.$store.state.managedAcademy + "/completed/" + this.selectedDocument._id
        )
        .then((response) => {
          this.selectedDocument = null;
          if (response) {
            this.getCompletedDocuments();
            this.archiveDialog = false;
            this.showAlert("success", this.$t("message.success"));
          }
        })
        .catch((e) => {
          this.loading = false;
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    filterCompletedDocuments() {
      try {
        this.filteredCompletedDocuments = [];
        if (!this.docNameFilter && !this.userFilter) {
          this.filteredCompletedDocuments = this.completedDocuments;
          return;
        }
        for (const d of this.completedDocuments) {
          if (this.docNameFilter) {
            const filter = this.docNameFilter.toLowerCase();
            const templateId = d.template ? d.template._id : null;
            if (filter == "main_tablet_waiver" && !templateId)
              this.filteredCompletedDocuments.push(d);
            else if (templateId && templateId == filter)
              this.filteredCompletedDocuments.push(d);
          } else if (this.userFilter) {
            const userName = d.user ? d.user.displayName : d.userName;
            if (userName && userName.indexOf(this.userFilter) >= 0)
              this.filteredCompletedDocuments.push(d);
          }
        }
      } catch (e) {
        this.filteredCompletedDocuments = [];
      }
    },
    goToUser(id) {
      if (id) {
        if (this.approvedMembers.indexOf(id) >= 0)
          this.navigateTo({ name: "viewmember", params: { memberid: id } });
        else window.open("https://connect.bjjlink.com/secure/profile/" + id);
      }
    },
    showPrintable() {
      window.open(
        "/secure/printwaiver/templates/main/" + this.selectedWaiverLang,
        "_blank"
      );
    },
    cancelAdding() {
      this.newWaiverLang = null;
      this.getTemplates();
    },
    addNewWaiverLang() {
      if (this.newWaiverLang && this.newWaiverLang != "") {
        if (this.languages.indexOf(this.newWaiverLang) < 0) {
          this.adding = true;
          this.selectedWaiverLang = this.newWaiverLang;
          this.waiverContents = this.$t("message.enter_text");
          this.templates.push({
            waiverContents: this.waiverContents,
            lang: this.newWaiverLang,
            waiverActive: true,
          });
        } else {
          this.showAlert("error", this.$t("message.already_present"));
        }
      }
    },
    updateWaiver() {
      const data = {
        contents: this.waiverContents,
        lang: this.selectedWaiverLang,
        active: this.waiverActive,
        extraFields: this.waiverExtraFields
      };
      Api()
        .patch(
          "admins/academy/" + this.$store.state.managedAcademy + "/waivers",
          data
        )
        .then((response) => {
          if (response) {
            this.newWaiverLang = null;
            this.getTemplates(this.selectedWaiverLang);
            this.showAlert("success", this.$t("message.success"));
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    removeWaiver() {
      Api()
        .delete(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/waivers/" +
          this.selectedWaiverLang
        )
        .then((response) => {
          if (response) {
            this.getTemplates();
            this.removeWaiverDialog = false;
            this.showAlert("success", this.$t("message.success"));
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    getTemplates(lang) {
      Api()
        .get("admins/academy/" + this.$store.state.selectedAcademy + "/waivers")
        .then((response) => {
          const data = response.data;
          if (data && data[0]) {
            this.templates = data;
            this.languages = [];
            for (const t of data) this.languages.push(t.lang);
          }
          this.swicthWaiver(lang);
        });
    },
    print(item) {
      window.open(Environment.get("VUE_APP_ASSETS_HOME") + "secure/printwaiver/" + item._id, "_blank");
    },
    viewDocument(item) {
      this.navigateTo({ name: 'viewcompleteddocument', params: { documentId: item._id }, })
    },
    goToDoc(id, name) {
      this.navigateTo({ name: "managedocument", params: { documentId: id }, crumbName: name || null });
    },
    goToPackage(id) {
      this.navigateTo({ name: "manageacademypackage", params: { academyPackageId: id } });
    },
    goToEmailTemplate(id) {
      this.navigateTo({ name: "manageemailtemplate", params: { emailTemplateId: id } });
    },
    getDocumentPackages() {
      Api()
        .get(
          "documents/academy/" + this.$store.state.managedAcademy + "/packages"
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            this.packages = data.packages;
          }
        });
    },
    getDocumentTemplates() {
      this.loadingDocuments = true;
      Api()
        .get(
          "documents/academy/" + this.$store.state.managedAcademy + "/templates"
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            this.documents = data;
            this.documentsList.push({
              text: this.$t("message.main_tablet_waiver"),
              value: "main_tablet_waiver",
            });
            if (this.documents) {
              for (const d of this.documents) {
                const text = d.name;
                const value = d._id;
                this.documentsList.push({ text: text, value: value });
              }
            }
            this.loadingDocuments = false;
          }
        });
    },
    swicthWaiver(newLang) {
      this.adding = false;
      if (!newLang) newLang = this.languages[0];
      if (newLang != this.selectedWaiverLang) {
        this.selectedWaiverLang = newLang;
        for (const w of this.templates) {
          if (w.lang == newLang && w.agreement) {
            this.selectedWaiver = w;
            if (w.agreement)
              this.waiverContents = w.agreement.replace(/<div/g, "<p")
            this.waiverActive = w.active == null || w.active ? true : false;
            if (this.selectedWaiver.extraFields) {
              this.waiverExtraFields = this.selectedWaiver.extraFields.map(function (f) {
                if (!f.fieldType) f.fieldType = "text";
                if (!f.options) f.options = [];
                return f;
              });
              if (this.waiverExtraFields.length > 0)
                this.displayExtraFieldsDiv = true;
            }
          }
        }
      }
    },
  },
};
</script>

<style></style>
